import { Icons } from '@/components/ui/icons'
import type { Icon } from '@/components/ui/icons'
import type { ActiveRole } from '@/lib/active-role'

export const APP_BRAND = {
  name: 'Procurement Command Center',
  tagline: 'Procurement Command Center',
} as const

export interface NavChild {
  href: string
  label: string
  description?: string
}

export interface NavItem {
  href?: string
  label: string
  icon: Icon
  children?: NavChild[]
  adminOnly?: boolean
  description?: string
}

/** 2 — Contract Lifecycle Management: ARCs and all other contracts live in one module (buyers and admins only) */
export const arcLifecycleNavItems: NavItem[] = [
  {
    label: 'Contract Lifecycle',
    icon: Icons.gavel,
    description: 'ARCs and contracts: repository, rates, amendments and obligations',
    children: [
      { href: '/clm', label: 'Contract Repository', description: 'All ARCs and contracts, upload and review' },
      { href: '/clm/rate-cards', label: 'Rate Card & Catalogue', description: 'Search and compare contracted rates' },
      { href: '/clm/opportunities', label: 'Contract Opportunities', description: 'Spend that could be covered by an ARC' },
      { href: '/clm/amendments', label: 'Amendments & Renewals', description: 'Lifecycle changes and renewals' },
      { href: '/clm/obligations', label: 'Obligations & Compliance', description: 'Obligations across the portfolio' },
      { href: '/clm/letters', label: 'Awards, LOI & LOA', description: 'Award contracts and letters of intent or award' },
      { href: '/clm/clauses', label: 'Clause Library', description: 'Standard clauses for contracts' },
    ],
  },
]

/** P5 E-Auction & Negotiation: auction committee, buyers, heads (view) and admins */
export const eAuctionNavItems: NavItem[] = [
  {
    label: 'E-Auction',
    icon: Icons.gavel,
    description: 'Auctions and negotiation',
    children: [
      { href: '/e-auction/workbench', label: 'Auction Workbench', description: 'Amenability, setup, scheduling, approvals and publishing' },
      { href: '/e-auction/live', label: 'Live Auction', description: 'Monitor ongoing auctions and bidding status' },
      { href: '/e-auction/results', label: 'Result and Analytics', description: 'Completed auctions, rankings, savings and participation' },
      { href: '/e-auction/negotiation', label: 'Negotiation', description: 'Negotiation rounds, counteroffers, committees and minutes' },
    ],
  },
]

/** Commercial comparison between Bid Evaluation and the NFA: landed-cost PCS, award scenarios and their approval. */
export const pcsNavItems: NavItem[] = [
  {
    label: 'PCS and Award',
    icon: Icons.scale,
    description: 'Landed-cost comparison and award scenarios',
    children: [
      { href: '/pcs', label: 'PCS Queue', description: 'Evaluations ready for commercial comparison and award scenarios' },
      { href: '/pcs/approvals', label: 'Award Approvals', description: 'Award scenarios waiting for your decision' },
    ],
  },
]

/** Award chain after Bid Evaluation: NFA, then purchase orders. Order Confirmation is a step inside the NFA, not a menu item. */
export const nfaNavItems: NavItem[] = [
  {
    label: 'NFA Workspace',
    icon: Icons.fileText,
    description: 'Approval notes for recommended awards',
    children: [
      { href: '/nfa/my', label: 'My NFAs', description: 'Drafts, submitted, returned and approved NFAs' },
      { href: '/nfa/queue', label: 'Approval Queue', description: 'NFAs waiting for your decision' },
    ],
  },
]

export const purchaseOrderNavItems: NavItem[] = [
  {
    label: 'Purchase Orders',
    icon: Icons.package,
    description: 'PO drafts through issuance and fulfilment',
    children: [
      { href: '/po/drafts', label: 'PO Drafts', description: 'Drafts, corrections and approved awards ready for a PO' },
      { href: '/po/all', label: 'All Purchase Orders', description: 'Every PO across its lifecycle' },
      { href: '/po/fulfilment', label: 'Fulfilment Tracking', description: 'Acknowledgement, amendments and delivery status' },
    ],
  },
]

/** Approval-only view of the same chain, for the Procurement Head. */
const headAwardNavItems: NavItem[] = [
  {
    label: 'PCS and Award',
    icon: Icons.scale,
    description: 'Approve award scenarios',
    children: [
      { href: '/pcs/approvals', label: 'Award Approvals', description: 'Award scenarios waiting for your decision' },
      { href: '/pcs', label: 'PCS Queue', description: 'Every PCS and where its award has got to' },
    ],
  },
  {
    label: 'NFA Workspace',
    icon: Icons.fileText,
    description: 'Approve award notes',
    children: [
      { href: '/nfa/queue', label: 'Approval Queue', description: 'NFAs waiting for your decision' },
      { href: '/nfa/my', label: 'My NFAs', description: 'All NFAs and their status' },
    ],
  },
  {
    label: 'Purchase Orders',
    icon: Icons.package,
    description: 'Release approvals and the PO register',
    children: [{ href: '/po/all', label: 'All Purchase Orders', description: 'Every PO across its lifecycle' }],
  },
]

/** Spend Analytics (Procurement Head) */
export const spendAnalyticsNavItem: NavItem = {
  label: 'Spend Analytics',
  icon: Icons.pieChart,
  description: 'Explore, investigate and act on spend',
  children: [
    { href: '/spend', label: 'Spend Explorer', description: 'Where the money goes' },
    { href: '/spend/compliance', label: 'Contract & Compliance', description: 'Are we buying as agreed' },
    { href: '/spend/savings', label: 'Savings Opportunities', description: 'Where we can pay less' },
    { href: '/spend/tracking', label: 'Savings Tracking', description: 'Committed and realised savings' },
    { href: '/spend/insights', label: 'AI Spend Insights', description: 'Ask questions, get answers' },
  ],
}

/** 2b — DPR Module */
/** 4 — Vendor performance lifecycle (admin) */
export const vpLifecycleNavItems: NavItem[] = [
  {
    label: 'Vendor Performance',
    icon: Icons.barChart,
    adminOnly: true,
    description: 'Vendor scorecards, evaluations and corrective actions',
    children: [
      { href: '/vpe/vendors', label: 'Vendor Scorecards', description: 'Every vendor with score, risk and evaluation status' },
      { href: '/vpe/evaluations', label: 'Evaluations', description: 'All evaluations, in review and approved' },
      { href: '/vpe/reevaluate', label: 'Re-evaluation', description: 'Vendors due or flagged for another evaluation' },
      { href: '/vpe/capa', label: 'CAPA', description: 'Corrective and preventive actions' },
      { href: '/avl', label: 'Approved Vendor List', description: 'Approved vendors, directory, contacts, POs and ARCs' },
    ],
  },
]

/** 5–9 — Intelligence & governance (admin order after lifecycles) */
export const intelligenceNavItems: NavItem[] = [
  {
    href: '/agent-hub',
    label: 'Agent Hub',
    icon: Icons.activity,
    adminOnly: true,
    description: 'Agents across PR, RFP, and vendor performance',
  },
  {
    href: '/ai/policies',
    label: 'AI Policies',
    icon: Icons.brain,
    adminOnly: true,
    description: 'Configure AI behavior',
  },
  {
    href: '/ai/insights',
    label: 'AI Insights',
    icon: Icons.lightbulb,
    adminOnly: true,
    description: 'Pattern detection & recommendations',
  },
]

export const systemNavItems: NavItem[] = [
  {
    href: '/settings',
    label: 'Settings',
    icon: Icons.settings,
    description: 'Personal preferences',
  },
]

export const adminNavItems: NavItem[] = [
  {
    label: 'Administration',
    icon: Icons.shield,
    adminOnly: true,
    description: 'User & system management',
    children: [
      { href: '/admin/users', label: 'Users & Roles', description: 'Manage users and permissions' },
      { href: '/admin/roles', label: 'Role Management', description: 'Realm and client roles' },
      { href: '/admin/groups', label: 'Groups & Teams', description: 'Organizational structure' },
      { href: '/admin/vendors', label: 'Vendor Directory', description: 'All registered vendors' },
      { href: '/admin/sessions', label: 'Active Sessions', description: 'User session management' },
      { href: '/admin/events', label: 'Security Events', description: 'Auth and security events' },
      { href: '/admin/master-data', label: 'Master Data', description: 'Upload vendors, plants, materials, approvals and contracts' },
      { href: '/admin/audit', label: 'Audit Logs', description: 'System activity trail' },
      { href: '/admin/settings', label: 'System Settings', description: 'Platform configuration' },
    ],
  },
]

export const vpeNavItems = vpLifecycleNavItems

/** Roles that see full PR → RFP → VP lifecycle nav (demo + deploy) */
export const FULL_LIFECYCLE_ROLES = [
  'admin',
  'user',
  'procurement_head',
  'procurement_team',
  'adani_procurement_officer',
  'adani_approver',
  'adani_capex_manager',
  'adani_tid_creator',
  'adani_engineering_lead',
] as const

export function isSystemAdmin(roles?: string[]): boolean {
  return !!roles?.includes('admin')
}

export function hasFullLifecycleAccess(roles?: string[]): boolean {
  if (isSystemAdmin(roles)) return true
  if (!roles?.length) return false
  return roles.some((r) => (FULL_LIFECYCLE_ROLES as readonly string[]).includes(r))
}

/** Full platform nav for the administrator: the indirect procurement flow in order, then AI governance and administration. */
export function getAdminNavItems(): NavItem[] {
  return [dashboardNavItem, ...indirectFlowNavItems, ...aiGovernanceNavItems, ...systemNavItems, ...adminNavItems]
}

/** New RFP lifecycle: requester drafting, buyer review and float, bid evaluation. */
const rfpLifecycleNavItem: NavItem = {
  label: 'RFP Lifecycle',
  icon: Icons.briefcase,
  description: 'Draft, review, float and evaluate RFPs',
  children: [
    { href: '/rfp/my', label: 'My RFPs', description: 'Track your RFPs' },
    { href: '/rfp/queue', label: 'RFP Queue', description: 'Review submitted RFPs, select suppliers and float' },
    { href: '/evaluation/bids', label: 'Bid Evaluation', description: 'Pre-qualification, technical and commercial review' },
  ],
}

const dashboardNavItem: NavItem = {
  href: '/',
  label: 'Dashboard',
  icon: Icons.dashboard,
  description: 'Overview & KPIs',
}

/** Purchase Requisition module for buyer and head: a single PRQ queue. */
const purchaseRequisitionNavItem: NavItem = {
  label: 'Purchase Requisition',
  icon: Icons.package,
  description: 'PR queue',
  children: [
    { href: '/pr/queue', label: 'PR Queue', description: 'All PRs (including From Catalogue), duplicates, consolidation' },
  ],
}

/** Group Estimation for buyers, estimators and the Category Lead. */
export const estimationNavItems: NavItem[] = [
  {
    label: 'Estimation',
    icon: Icons.target,
    description: 'Internal estimates for approved RFPs',
    children: [
      { href: '/estimation', label: 'Estimate Queue', description: 'Estimates to prepare, review and approve' },
      { href: '/estimation/dashboard', label: 'Estimator KPIs', description: 'Turnaround, variation and workload' },
      { href: '/estimation/exclusions', label: 'Exclusions', description: 'Requests to leave an RFP out of estimation' },
    ],
  },
]

const onboardingLink = (queue: string, label: string, description: string) => ({ href: `/vendor-onboarding/work/${queue}`, label, description })
const onboardingAllQueue = { href: '/vendor-onboarding/queue', label: 'Onboarding Queue', description: 'Every application, from EOI to active vendor' }
const onboardingDiscovery = { href: '/vendor-onboarding/discovery', label: 'Vendor Directory', description: 'Search qualified vendors by classification' }
const onboardingNotifications = { href: '/vendor-onboarding/notifications', label: 'Notifications', description: 'Stage changes that need your team' }

/** Buyer view of vendor onboarding, from the EOI review to the vendor code and the vendor lifecycle. */
export const vendorOnboardingReviewNavItems: NavItem[] = [
  {
    label: 'Vendor Onboarding',
    icon: Icons.users,
    description: 'Review applications, run qualification and issue vendor codes',
    children: [
      onboardingAllQueue,
      onboardingDiscovery,
      onboardingNotifications,
    ],
  },
]

/** Sidebar for each onboarding reviewer role. The queue page carries that role's work queues. */
const onboardingRoleNav = (_queue: string, _label: string, description: string): NavItem[] => [
  {
    label: 'Vendor Onboarding',
    icon: Icons.users,
    description,
    children: [onboardingAllQueue, onboardingDiscovery, onboardingNotifications],
  },
]

/** Vendor portal: invited suppliers bid on floated RFPs. */
const vendorBidNavItems: NavItem[] = [
  {
    label: 'Vendor Onboarding',
    icon: Icons.messageSquare,
    description: 'Register your company as a vendor',
    children: [{ href: '/vendor/onboarding', label: 'Vendor Onboarding', description: 'Chat with the assistant and share your documents' }],
  },
  {
    label: 'RFP Bidding',
    icon: Icons.briefcase,
    description: 'Respond to RFPs you are invited to',
    children: [
      { href: '/vendor/rfps', label: 'RFP Invitations', description: 'Read the RFP, upload documents and submit your bid' },
    ],
  },
  {
    label: 'Orders',
    icon: Icons.package,
    description: 'Awards you have won and purchase orders issued to you',
    children: [{ href: '/vendor/orders', label: 'Awards & Purchase Orders', description: 'Award notices, issued POs and acknowledgement' }],
  },
]

/** Direct procurement (admin view): PR to PO against rate contracts, no RFP or RFQ. Every decision runs through AI Policies. */
export const directProcurementNavItems: NavItem[] = [
  { href: '/', label: 'Dashboard', icon: Icons.dashboard, description: 'Direct procurement overview and AI insights' },
  {
    label: 'Purchase Requisition',
    icon: Icons.package,
    description: 'Raise PRs and approve them as buyer',
    children: [
      { href: '/direct/pr/new', label: 'New PR', description: 'Describe the need and pick catalog items' },
      { href: '/direct/pr/my', label: 'My PRs', description: 'Requisitions you raised' },
      { href: '/direct/pr/queue', label: 'PR Queue', description: 'Buyer approval, vendor recommendation and PO draft' },
    ],
  },
  {
    label: 'Catalogue',
    icon: Icons.tag,
    description: 'Catalogue and price list',
    children: [{ href: '/direct/catalog', label: 'Catalogue & Price List', description: 'Every item with the contracted rate from each vendor' }],
  },
  {
    label: 'Purchase Orders',
    icon: Icons.fileText,
    description: 'Draft, review, approve, send and receive',
    children: [
      { href: '/direct/po/drafts', label: 'Draft POs', description: 'Edit and review drafts before sending' },
      { href: '/direct/po/approvals', label: 'For Approval', description: 'POs the AI policy routed to an approver' },
      { href: '/direct/po/all', label: 'All POs', description: 'Every PO from draft to closed' },
    ],
  },
  {
    label: 'Vendors',
    icon: Icons.building,
    description: 'Vendor directory and approved vendors',
    children: [
      { href: '/direct/vendors', label: 'Vendor Directory', description: 'Every vendor, approved or pending' },
      { href: '/direct/vendors/approved', label: 'Approved Vendor List', description: 'Approved vendors with a valid rate contract' },
    ],
  },
  {
    label: 'Contracts (ARCs)',
    icon: Icons.gavel,
    description: 'Annual rate contracts and utilisation',
    children: [
      { href: '/direct/contracts/active', label: 'Active Contracts', description: 'Contracts in force today' },
      { href: '/direct/contracts/expiring', label: 'Expiring Soon', description: 'Ending within 90 days' },
      { href: '/direct/contracts/expired', label: 'Expired Contracts', description: 'Past their validity date' },
      { href: '/direct/contracts/renewals', label: 'Renewals and Drafts', description: 'Prepared for the next period' },
      { href: '/direct/contracts', label: 'All Contracts', description: 'Every rate contract' },
    ],
  },
  { href: '/ai/policies', label: 'AI Policies', icon: Icons.brain, description: 'Rules that drive every direct procurement decision' },
]

/** Minimal per-role sidebars for the Purchase Requisition module. */
/** Approved Vendor List for buyers and the procurement head. */
export const approvedVendorNavItems: NavItem[] = [
  {
    label: 'Vendor Directory',
    icon: Icons.building,
    description: 'Approved vendors, qualified pool, changes and exceptions',
    children: [
      { href: '/avl', label: 'Approved Vendor List', description: 'Listed vendors, the qualified pool and every vendor profile' },
    ],
  },
]

export function getRoleNavItems(activeRole: ActiveRole): NavItem[] | null {
  switch (activeRole) {
    case 'user':
      return [
        dashboardNavItem,
        {
          label: 'Purchase Requisition',
          icon: Icons.messageSquare,
          description: 'Raise and track your requisitions',
          children: [
            { href: '/pr/new', label: 'New PR', description: 'Create a PR by chatting with the AI' },
            { href: '/pr/my', label: 'My PRs', description: 'Track your requisitions' },
            { href: '/direct/pr/my', label: 'My Catalogue PRs', description: 'Track requisitions raised From Catalogue' },
          ],
        },
        {
          label: 'Vendor Directory',
          icon: Icons.building,
          description: 'Ask for a review of the vendors in a category',
          children: [{ href: '/avl/requests', label: 'Review Requests', description: 'Tell the procurement team a category needs vendors' }],
        },
        {
          label: 'RFP Lifecycle',
          icon: Icons.briefcase,
          description: 'Draft and track your RFPs',
          children: [
            { href: '/rfp/my', label: 'My RFPs', description: 'Track your RFPs' },
            { href: '/evaluation/bids', label: 'Bid Evaluation', description: 'Technical evaluation of received bids' },
          ],
        },
      ]
    case 'buyer':
      return [
        dashboardNavItem,
        purchaseRequisitionNavItem,
        {
          label: 'RFP Lifecycle',
          icon: Icons.briefcase,
          description: 'Review, approve and float RFPs',
          children: [
            { href: '/rfp/queue', label: 'RFP Queue', description: 'Review submitted RFPs, select suppliers and float' },
            { href: '/evaluation/bids', label: 'Bid Evaluation', description: 'Pre-qualification, technical and commercial review' },
          ],
        },
        ...vendorOnboardingReviewNavItems,
        ...approvedVendorNavItems,
        ...estimationNavItems,
        ...pcsNavItems,
        ...nfaNavItems,
        ...purchaseOrderNavItems,
        ...eAuctionNavItems,
        ...arcLifecycleNavItems,
      ]
    case 'head':
      return [dashboardNavItem, purchaseRequisitionNavItem, ...approvedVendorNavItems, ...estimationNavItems, ...headAwardNavItems, ...onboardingRoleNav('approvals', 'Approvals', 'Onboarding exceptions, special routes and blocks'), spendAnalyticsNavItem, ...eAuctionNavItems]
    case 'auction_committee':
      return [
        ...eAuctionNavItems,
        {
          label: 'Bid Evaluation',
          icon: Icons.target,
          description: 'Evaluation outcomes feeding the auction',
          children: [{ href: '/evaluation/bids', label: 'Bid Evaluation', description: 'Pre-qualification, technical and commercial review' }],
        },
        ...systemNavItems,
      ]
    case 'vendor':
      return [dashboardNavItem, ...vendorBidNavItems, ...systemNavItems]
    case 'direct_admin':
      return directProcurementNavItems
    case 'hr_kam':
      return onboardingRoleNav('kam', 'KAM Review', 'Key account arrangements waiting for HR')
    case 'gcc_sm':
      return [
        ...onboardingRoleNav('deviations', 'Deviation Review', 'Questionnaires with deviations'),
        { label: 'Vendor Updates', icon: Icons.pencil, description: 'Vendor changes waiting for review', children: [onboardingLink('updates', 'Vendor Update Review', 'Bank, tax and master detail changes')] },
      ]
    case 'gcc_tpdd':
      return [
        ...onboardingRoleNav('tpdd', 'TPDD Review', 'Hard and individual TPDD, re-assessments'),
        { label: 'TPDD Expiry', icon: Icons.clock, description: 'Expired and expiring due diligence', children: [onboardingLink('expiry', 'TPDD Expiry', 'Vendors due for re-assessment')] },
      ]
    case 'gcc_mdg':
      return onboardingRoleNav('mdg', 'MDG Review', 'IRQs to validate and vendor codes to create')
    case 'cft_member':
      return onboardingRoleNav('cft', 'CFT Assessment', 'Suppliers waiting for your team')
    case 'vom':
      return onboardingRoleNav('governance', 'Governance Exceptions', 'Suspensions and matrix rejections')
    case 'approver':
      return onboardingRoleNav('approvals', 'Approvals', 'Requests waiting for your approval')
    default:
      return null
  }
}

/** One approvals inbox for every request type, placed right under the dashboard for internal roles. */
const approvalsInboxNavItem: NavItem = {
  href: '/approvals',
  label: 'Approvals',
  icon: Icons.clipboardCheck,
  description: 'Everything waiting on you and everything you raised',
}

export function getSidebarNavItems(roles?: string[], activeRole?: ActiveRole): NavItem[] {
  const items = baseSidebarNavItems(roles, activeRole)
  if (activeRole === 'vendor' || items.some((i) => i.href === approvalsInboxNavItem.href)) return items
  const at = items.findIndex((i) => i.href === '/')
  items.splice(at + 1, 0, approvalsInboxNavItem)
  return items
}

function baseSidebarNavItems(roles?: string[], activeRole?: ActiveRole): NavItem[] {
  if (activeRole) {
    const roleItems = getRoleNavItems(activeRole)
    if (roleItems) return roleItems
  }
  if (isSystemAdmin(roles)) {
    return getAdminNavItems()
  }
  if (hasFullLifecycleAccess(roles)) {
    return [
      dashboardNavItem,
      purchaseRequisitionNavItem,
      rfpLifecycleNavItem,
      ...vpLifecycleNavItems,
      ...intelligenceNavItems,
      ...systemNavItems,
    ]
  }
  return [dashboardNavItem, purchaseRequisitionNavItem, rfpLifecycleNavItem, ...systemNavItems]
}

export function getDisplayRole(roles?: string[]): string {
  if (roles?.includes('admin')) return 'System Administrator'
  if (roles?.includes('procurement_head')) return 'Procurement Head'
  if (roles?.includes('user')) return 'Procurement Analyst'
  return 'Procurement Head'
}

/**
 * Indirect procurement, in the order the business runs it. Order follows the scope document.
 * Each group lists only screens that exist; what a stage does beyond them happens inside those screens.
 */
export const indirectFlowNavItems: NavItem[] = [
  {
    label: 'Vendor Onboarding',
    icon: Icons.users,
    adminOnly: true,
    description: 'Registration, due diligence, qualification and approval',
    children: [
      { href: '/vendor/onboarding', label: 'Vendor Portal View', description: 'Register a vendor the way a vendor does it' },
      onboardingAllQueue,
    ],
  },
  {
    label: 'Vendor Directory',
    icon: Icons.building,
    adminOnly: true,
    description: 'Approved vendors and everything about each one',
    children: [
      { href: '/avl', label: 'Approved Vendor List', description: 'Listed vendors, the qualified pool and every vendor profile' },
    ],
  },
  {
    label: 'Demand Planning',
    icon: Icons.trendingUp,
    description: 'Plan demand, allocate and consolidate it, and route it to sourcing',
    children: [
      { href: '/demand/plan', label: 'Demand Plan', description: 'Build or amend a demand plan in a chat' },
      { href: '/demand/hub', label: 'Demand Hub', description: 'Allocation, consolidation and sourcing packages' },
      { href: '/demand/repository', label: 'Plan Repository', description: 'Every demand plan, its versions and status' },
    ],
  },
  {
    label: 'Purchase Requisition',
    icon: Icons.package,
    description: 'Raise, track and approve PRs',
    children: [
      { href: '/pr/new', label: 'New PR', description: 'Raise a purchase requisition' },
      { href: '/pr/my', label: 'My PRs', description: 'Track PRs you raised' },
      { href: '/pr/queue', label: 'PR Queue', description: 'Duplicates, consolidation and contract check' },
    ],
  },
  {
    label: 'Catalogue',
    icon: Icons.tag,
    description: 'Catalogue and price list',
    children: [{ href: '/clm/rate-cards', label: 'Catalogue & Price List', description: 'Search and compare contracted rates' }],
  },
  {
    label: 'Sourcing (RFP)',
    icon: Icons.briefcase,
    description: 'Draft, float and evaluate RFPs, then auction',
    children: [
      { href: '/sourcing/strategy', label: 'AI Sourcing Strategy', description: 'Get AI route recommendation: Open vs. Limited vs. Single Source' },
      { href: '/rfp/my', label: 'My RFPs', description: 'Track your RFPs' },
      { href: '/rfp/queue', label: 'RFP Queue', description: 'Review, select suppliers and float' },
      { href: '/evaluation/bids', label: 'Bid Evaluation', description: 'Technical and commercial evaluation' },
      { href: '/e-auction/committee', label: 'Auction Committee', description: 'Create the committee for an auction' },
      { href: '/e-auction/workbench/strategy', label: 'Auction Strategy', description: 'Choose how each auction is run' },
    ],
  },
  ...estimationNavItems,
  ...pcsNavItems,
  {
    label: 'NFA',
    icon: Icons.fileText,
    description: 'Comparative approval of the recommended award',
    children: [
      { href: '/nfa/my', label: 'My NFAs', description: 'Drafts, submitted, returned and approved NFAs' },
      { href: '/nfa/queue', label: 'Approval Queue', description: 'NFAs waiting for your decision' },
    ],
  },
  {
    label: 'Purchase Orders',
    icon: Icons.package,
    description: 'PO drafts through issuance and fulfilment',
    children: [
      { href: '/po/drafts', label: 'PO Drafts', description: 'Drafts, corrections and approved awards ready for a PO' },
      { href: '/po/all', label: 'All Purchase Orders', description: 'Every PO across its lifecycle' },
      { href: '/po/fulfilment', label: 'Fulfilment Tracking', description: 'Acknowledgement, amendments and delivery status' },
    ],
  },
  {
    label: 'Contract Management',
    icon: Icons.gavel,
    description: 'Contracts, obligations, renewals and opportunities',
    children: [
      { href: '/clm', label: 'Contract Repository', description: 'Every contract with its terms, clauses and versions' },
      { href: '/clm/obligations', label: 'Obligations', description: 'Obligations across the portfolio' },
      { href: '/clm/letters', label: 'Awards, LOI & LOA', description: 'Award contracts and letters of intent or award' },
      { href: '/clm/clauses', label: 'Clause Library', description: 'Standard clauses for contracts' },
      { href: '/clm/amendments', label: 'Amendments & Renewals', description: 'Lifecycle changes and renewals' },
      { href: '/clm/opportunities', label: 'Contract Opportunities', description: 'Spend that could be covered by a contract' },
    ],
  },
  {
    label: 'Vendor Performance',
    icon: Icons.barChart,
    adminOnly: true,
    description: 'Performance score of every vendor',
    children: [{ href: '/vpe/vendors', label: 'Vendor Performance Evaluation', description: 'Every vendor with its performance score' }],
  },
  {
    label: 'Savings',
    icon: Icons.trendingUp,
    description: 'Committed and realised savings',
    children: [
      { href: '/spend/tracking', label: 'Savings Tracking', description: 'Committed and realised savings' },
      { href: '/spend/savings', label: 'Savings Opportunities', description: 'Where we can pay less' },
    ],
  },
  {
    label: 'Spend Analytics',
    icon: Icons.pieChart,
    description: 'Where the money goes and how it compares with contracts',
    children: [
      { href: '/spend', label: 'Spend Dashboard', description: 'Where the money goes' },
      { href: '/spend/compliance', label: 'Contract & Compliance', description: 'Are we buying as agreed' },
    ],
  },
]

/** Governance of the AI itself, kept apart from the procurement flow. */
const aiGovernanceNavItems: NavItem[] = [
  {
    label: 'AI Governance',
    icon: Icons.brain,
    adminOnly: true,
    description: 'Agents, policies and insights',
    children: [
      { href: '/agent-hub', label: 'Agent Hub', description: 'How every agent works, module by module' },
      { href: '/ai/policies', label: 'AI Policies', description: 'Rules the AI follows at every step' },
      { href: '/ai/insights', label: 'AI Insights', description: 'Pattern detection and recommendations' },
    ],
  },
]
